import { Button, Hidden } from '@material-ui/core';
import { BoletaEstadoIcon, Typography } from '@saesa/ui-kit-front';
import { TipoPagoBoleta } from 'config';
import { obtenerTextoAvisoPagoCC, obtenerTextoAvisoPagoCCPagado, obtenerTextoAvisoPagoCCPendientePago } from 'helper/trackingCC.helper';
import { AvisoPagoModel } from 'models';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import FormatValues from 'utils/FormatValues';
import { tagFacturacionBotonPago } from 'utils/gtmEventsUtils';
import TrackingService from 'services/TrackingService';
import { useSnackbar } from 'notistack';
import { exportIconoBlanco } from 'assets/images';
import useStyles from './AvisoPagoComercialStyles';

const AvisoPagoComercial = ({ className, modeloAvisoPago }: { className?: string; modeloAvisoPago: AvisoPagoModel }) => {
  const { fechaEmision, fechaPago, fechaVencimiento, monto, tipoPago } = modeloAvisoPago;

  /**
   * Uso del i18n para traducciones
   */
  const { t } = useTranslation();

  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();
  /**
   * Selector de redux
   */
  const usuario = useSelector((state: RootState) => state.tracking.usuario);
  /**
   * Componente de pildora
   */
  const { enqueueSnackbar } = useSnackbar();
  /**
   * URL base de pago
   */
  const urlPagoRoot = new URL(process.env.REACT_APP_URL_PAGO || '');

  /**
   * Obtiene la URL de onboarding desde variable de entorno
   */
  const obtenerUrlOnboarding = () => process.env.REACT_APP_URL_ONBOARDING;

  const onClickPago = () => {
    const data = {
      numero: usuario.numeroServicio,
    };
    tagFacturacionBotonPago(data);

    TrackingService.validarMedioPago(usuario.numeroServicio)
      .then(response => {
        urlPagoRoot.searchParams.set('id', response?.idPago);
        window.location.replace(urlPagoRoot.toString());
      })
      .catch(error => {
        enqueueSnackbar(error?.response?.data?.mensaje, {
          variant: 'error',
        });
      });
  };

  /**
   * Valida con un switch el estado actual
   * @returns Retorna un RactNode
   */
  const contenidoPorTipo = () => {
    switch (tipoPago) {
      case TipoPagoBoleta.VENCIDO:
        return (
          <>
            <section className={styles.avisoCntFitContent}>
              <BoletaEstadoIcon className={styles.iconError} />
              <section className={styles.infoErrorCnt}>
                <Typography className={styles.label} strong="semi-bold" variant="parrafo">
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.pagoPendiente')}
                </Typography>
                <Typography className={styles.info} strong="normal" variant="parrafo">
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.mantenBoletaAlDia')}
                </Typography>
              </section>
              <section className={styles.contenedorBoton}>
                <Button className={styles.botonIr} onClick={onClickPago} variant="contained" color="primary" type="submit">
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.btnIniciarPago')}
                  <img src={exportIconoBlanco} alt="export" className={styles.iconExport} />
                </Button>
              </section>
            </section>
            <section className={styles.seccionOficinaVirtual}>
              <Typography className={styles.textoOficinaVirtual} variant="parrafo">
                <span className={styles.textoParteUno}>
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.textoOficinaVirtual')}
                </span>{' '}
                <a href={obtenerUrlOnboarding()} target="_blank" rel="noreferrer" className={styles.linkOficinaVirtual}>
                  <span className={styles.textoLinkOficinaVirtual}>
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.linkOficinaVirtual')}
                  </span>
                </a>
              </Typography>
            </section>
          </>
        );
      case TipoPagoBoleta.PENDIENTE_PAGO:
        return (
          <>
            <section className={styles.avisoCnt}>
              <Hidden only={['md', 'lg', 'xl']}>
                <section className={styles.vencimientoContenedor}>
                  {obtenerTextoAvisoPagoCCPendientePago(fechaEmision, fechaVencimiento, styles, t)}
                  <section className={styles.vencimientoMonto}>
                    <Typography className={styles.label} strong="semi-bold" variant="parrafo">
                      {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.monto')}
                    </Typography>
                    {obtenerTextoAvisoPagoCC(FormatValues.money(monto), styles, t, 'semi-bold')}
                  </section>
                </section>
                <section className={styles.vencimientoBoton}>
                  <Button className={styles.botonIr} onClick={onClickPago} variant="contained" color="primary" type="submit">
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.btnIniciarPago')}
                    <img src={exportIconoBlanco} alt="export" className={styles.iconExport} />
                  </Button>
                </section>
              </Hidden>
              <Hidden only={['xs', 'sm']}>
                {obtenerTextoAvisoPagoCCPendientePago(fechaEmision, fechaVencimiento, styles, t)}
                <section className={styles.vencimientoMonto}>
                  <Typography className={styles.label} strong="semi-bold" variant="parrafo">
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.monto')}
                  </Typography>
                  {obtenerTextoAvisoPagoCC(FormatValues.money(monto), styles, t, 'semi-bold')}
                </section>
                <section className={styles.vencimientoBoton}>
                  <Button className={styles.botonIr} onClick={onClickPago} variant="contained" color="primary" type="submit">
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.btnIniciarPago')}
                    <img src={exportIconoBlanco} alt="export" className={styles.iconExport} />
                  </Button>
                </section>
              </Hidden>
            </section>
            <section className={styles.seccionOficinaVirtual}>
              <Typography className={styles.textoOficinaVirtual} variant="parrafo">
                <span className={styles.textoParteUno}>
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.textoOficinaVirtual')}
                </span>{' '}
                <a href={obtenerUrlOnboarding()} target="_blank" rel="noreferrer" className={styles.linkOficinaVirtual}>
                  <span className={styles.textoLinkOficinaVirtual}>
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.linkOficinaVirtual')}
                  </span>
                </a>
              </Typography>
            </section>
          </>
        );
      case null:
      case TipoPagoBoleta.PAGADO:
        return (
          <>
            <section className={styles.avisoCnt}>
              <section className={styles.pagadoIcono}>
                <BoletaEstadoIcon className={styles.iconSuccess} />
                <Typography className={styles.label} strong="semi-bold" variant="parrafo">
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.boletaPagada')}
                </Typography>
              </section>
              <Hidden only={['md', 'lg', 'xl']}>
                <section className={styles.contenedorMobile}>{obtenerTextoAvisoPagoCCPagado(fechaPago, monto, styles, t)}</section>
              </Hidden>
              <Hidden only={['xs', 'sm']}>{obtenerTextoAvisoPagoCCPagado(fechaPago, monto, styles, t)}</Hidden>
            </section>
            <section className={styles.seccionOficinaVirtual}>
              <Typography className={styles.textoOficinaVirtual} variant="parrafo">
                <span className={styles.textoParteUno}>
                  {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.textoOficinaVirtual')}
                </span>{' '}
                <a href={obtenerUrlOnboarding()} target="_blank" rel="noreferrer" className={styles.linkOficinaVirtual}>
                  <span className={styles.textoLinkOficinaVirtual}>
                    {t('tracking:paginas.tracking.cicloFacturacion.avisoPago.textos.linkOficinaVirtual')}
                  </span>
                </a>
              </Typography>
            </section>
          </>
        );
      default:
        return <></>;
    }
  };

  /**
   * Obtiene contenido a renderizar
   */
  const [renderContenido, setRenderContenido] = useState(contenidoPorTipo());

  useEffect(() => {
    setRenderContenido(contenidoPorTipo());
  }, []);

  return (
    <section data-testid="map-container" className={className}>
      {renderContenido}
    </section>
  );
};

AvisoPagoComercial.defaultProps = {
  className: '',
};

export default AvisoPagoComercial;
